/**
 * NativeAlarmBridge.ts — Direct Android JavascriptInterface bridge.
 * Calls window.NativeAlarm.* methods directly — NO Capacitor plugin system.
 * 
 * JavascriptInterface methods are SYNCHRONOUS (return string immediately).
 * Much more reliable than Capacitor's async plugin bridge.
 */
import { isNativeApp } from '../utils/platform';

// Type definition for the native bridge
interface NativeAlarmInterface {
  scheduleInterval(json: string): string;
  scheduleDaily(json: string): string;
  scheduleOnDates(json: string): string;
  cancel(type: string): string;
  cancelAll(): string;
  getStatus(type: string): string;
  showNotification(json: string): string;
  startService(json: string): string;
  stopService(): string;
  // Sadhana-specific (separate from Divine Reminder)
  scheduleSadhanaAlarms(json: string): string;
  cancelSadhanaAlarms(): string;
  getSadhanaStatus(): string;
}

// Access the native bridge
function getBridge(): NativeAlarmInterface | null {
  if (!isNativeApp()) return null;
  const bridge = (window as any).NativeAlarm;
  if (!bridge) {
    console.warn('[NativeAlarm] window.NativeAlarm not available');
    return null;
  }
  return bridge as NativeAlarmInterface;
}

// Check if native bridge is available
export function isNativeAlarmAvailable(): boolean {
  return isNativeApp() && !!(window as any).NativeAlarm;
}

// Parse JSON result from native
function parseResult(jsonStr: string): any {
  try {
    return JSON.parse(jsonStr);
  } catch {
    return { success: false, message: 'JSON parse failed' };
  }
}

// ─── Divine Reminder (interval-based mantra alarm) ────────────
export function scheduleNativeDivineReminder(
  intervalMinutes: number, mantraText: string, soundName: string
) {
  const bridge = getBridge();
  if (!bridge) return { success: false, type: 'divine_reminder', alarmsScheduled: 0, message: 'Bridge not available' };

  const result = bridge.scheduleInterval(JSON.stringify({
    type: 'divine_reminder',
    intervalMinutes,
    title: 'दिव्य स्मरण',
    body: mantraText,
    soundName,
  }));
  console.log('[NativeAlarm] scheduleInterval result:', result);
  return parseResult(result);
}

export function cancelNativeDivineReminder() {
  const bridge = getBridge();
  if (!bridge) return { success: false, type: 'divine_reminder' };
  return parseResult(bridge.cancel('divine_reminder'));
}

// ─── Jaap Incomplete (interval-based) ───
export function scheduleNativeJaapIncomplete(
  intervalMinutes: number, mantraText: string, soundName: string
) {
  const bridge = getBridge();
  if (!bridge) return { success: false, type: 'jaap_incomplete', alarmsScheduled: 0 };

  const result = bridge.scheduleInterval(JSON.stringify({
    type: 'jaap_incomplete',
    intervalMinutes,
    title: 'जाप अधूरा है',
    body: `${mantraText} — माला पूरी करें`,
    soundName,
  }));
  return parseResult(result);
}

export function cancelNativeJaapIncomplete() {
  const bridge = getBridge();
  if (!bridge) return { success: false, type: 'jaap_incomplete' };
  return parseResult(bridge.cancel('jaap_incomplete'));
}

// ─── Sadhana Reminder (daily fixed times — ForegroundService) ──
export function scheduleNativeSadhanaAlarms(
  times: string[], bodyText: string, soundName: string, vibrate: boolean
) {
  const bridge = getBridge();
  if (!bridge) return { success: false, type: 'sadhana', alarmsScheduled: 0, message: 'Bridge not available' };

  const result = bridge.scheduleSadhanaAlarms(JSON.stringify({
    times,
    title: 'साधना स्मरण',
    body: bodyText || 'राम नाम जपने का समय हो गया है 🙏',
    soundName: soundName || 'default',
    vibrate,
  }));
  console.log('[NativeAlarm] scheduleSadhanaAlarms result:', result);
  return parseResult(result);
}

export function cancelNativeSadhanaAlarms() {
  const bridge = getBridge();
  if (!bridge) return { success: false, type: 'sadhana' };
  return parseResult(bridge.cancelSadhanaAlarms());
}

export function getNativeSadhanaStatus() {
  const bridge = getBridge();
  if (!bridge) return { enabled: false, times: '', soundName: 'default', vibrate: true };
  return parseResult(bridge.getSadhanaStatus());
}

// ─── Sadhana Reminder (OLD — kept for backward compatibility) ─
export function scheduleNativeSadhana(hour: number, minute: number, bodyText: string) {
  const bridge = getBridge();
  if (!bridge) return { success: false, type: 'sadhana', alarmsScheduled: 0 };

  const result = bridge.scheduleDaily(JSON.stringify({
    type: 'sadhana',
    hour,
    minute,
    title: 'साधना स्मरण',
    body: bodyText || 'आज की साधना का समय हो गया है',
    soundName: 'default',
  }));
  return parseResult(result);
}

export function cancelNativeSadhana() {
  const bridge = getBridge();
  if (!bridge) return { success: false, type: 'sadhana' };
  return parseResult(bridge.cancel('sadhana'));
}

// ─── Shlok of the Day (daily fixed time) ──────────────────────
export function scheduleNativeShlok(hour: number, minute: number) {
  const bridge = getBridge();
  if (!bridge) return { success: false, type: 'shlok', alarmsScheduled: 0 };

  const result = bridge.scheduleDaily(JSON.stringify({
    type: 'shlok',
    hour,
    minute,
    title: 'श्लोक ऑफ़ द डे',
    body: 'आज का श्लोक पढ़ें',
    soundName: 'default',
  }));
  return parseResult(result);
}

export function cancelNativeShlok() {
  const bridge = getBridge();
  if (!bridge) return { success: false, type: 'shlok' };
  return parseResult(bridge.cancel('shlok'));
}

// ─── Ekadashi Reminder (date-based) ────────────
export function scheduleNativeEkadashi(timestamps: number[]) {
  const bridge = getBridge();
  if (!bridge) return { success: false, type: 'ekadashi', alarmsScheduled: 0 };

  const result = bridge.scheduleOnDates(JSON.stringify({
    type: 'ekadashi',
    timestamps,
    title: 'एकादशी स्मरण',
    body: 'कल एकादशी है, व्रत की तैयारी करें',
    soundName: 'default',
  }));
  return parseResult(result);
}

export function cancelNativeEkadashi() {
  const bridge = getBridge();
  if (!bridge) return { success: false, type: 'ekadashi' };
  return parseResult(bridge.cancel('ekadashi'));
}

// ─── Cancel everything ────────────────────────────────────────
export function cancelAllNativeAlarms() {
  const bridge = getBridge();
  if (!bridge) return { success: false };
  return parseResult(bridge.cancelAll());
}

// ─── Show immediate notification (for minimized state) ────────
export function showNativeNotification(title: string, body: string) {
  const bridge = getBridge();
  if (!bridge) return { success: false };
  return parseResult(bridge.showNotification(JSON.stringify({ title, body, id: 7777 })));
}

// ─── Get status ────────────────────────────────────────────────
export function getNativeAlarmStatus(type: string) {
  const bridge = getBridge();
  if (!bridge) return { enabled: false, mode: '', intervalMinutes: 0 };
  return parseResult(bridge.getStatus(type));
}

// ─── Start ForegroundService (99.9% reliable reminders) ────────
export function startNativeService(intervalMinutes: number, mantraText: string, soundName: string) {
  const bridge = getBridge();
  if (!bridge) return { success: false, message: 'Bridge not available' };
  return parseResult(bridge.startService(JSON.stringify({
    intervalMinutes,
    title: 'दिव्य स्मरण',
    body: mantraText,
    soundName,
  })));
}

// ─── Stop ForegroundService ────────────────────────────────────
export function stopNativeService() {
  const bridge = getBridge();
  if (!bridge) return { success: false };
  return parseResult(bridge.stopService());
}
